
import { defineComponent, nextTick, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useForm, useField } from "vee-validate";
import { KhoService } from "@/core/services/Kho.service";

import { CODE_REGEX } from "@/core/constants/const";
import {
  getErrorMsg,
  swalErrNotification,
  swalNotification,
} from "@/core/helpers/utils";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { useLoading } from "@/core/hooks/use-loading";
import { useCreatingForm } from "@/core/hooks/use-creating-form";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import { useButtonCustom } from "../../../../core/hooks/use-button-custom";
import {
  IKhoResponseBase,
  ITaiKhoanResponseBase,
} from "../../../../core/interfaces/ApiResponses";

import * as Yup from "yup";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import DivCol from "@/layout/_shared/DivCol.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import TaiKhoanSelectionV2 from "@/components/shared/tai-khoan-selection/TaiKhoanSelectionV2.vue";
import DanhSachKhoTable from "@/views/crafted/pages/kho/danh-sach-kho-table/DanhSachKhoTable.vue";

export default defineComponent({
  name: "new-warehouses",

  components: {
    ButtonCustom,
    DanhSachKhoTable,
    PageContentBase,
    DivRow,
    DivCol,
    LabelCustom,
    TaiKhoanSelectionV2,
  },

  setup() {
    const { goBack } = useRouterCustom();
    const router = useRouter();
    const store = useStore();
    const { startLoading, endLoading } = useLoading();
    const { isCreating } = useCreatingForm();
    const { ButtonsType, ButtonTypeColors } = useButtonCustom();
    document.title = "Tạo mới - Kho - Đường sắt Hà Nội";

    const tenKhoInputRef = ref<{ focus: () => void } | null>(null);
    const khoItems = ref<IKhoResponseBase[]>([]);
    const taiKhoanSelectionKey = ref(0);

    const newWarehouseValidator = Yup.object({
      name: Yup.string().required("Tên kho là bắt buộc"),
      code: Yup.string()
        .matches(CODE_REGEX, "Mã kho phải đúng định dạng")
        .required("Mã kho là bắt buộc"),
      address: Yup.string().required("Địa chỉ là bắt buộc"),
    });

    const { validate, resetForm } = useForm({
      validationSchema: newWarehouseValidator,
    });

    const { value: name, errorMessage: nameErrorMess } =
      useField<string>("name");
    const { value: code, errorMessage: codeErrorMess } =
      useField<string>("code");
    const { value: note } = useField<string>("note");
    const { value: address, errorMessage: addressErrorMess } =
      useField<string>("address");

    const taiKhoanData = ref<{
      id: number;
      maTaiKhoan: string;
      maChiTiet?: string;
      tenTaiKhoan: string;
    }>({
      id: 0,
      maTaiKhoan: "",
      tenTaiKhoan: "",
    });

    const onSelectTaiKhoan = ({
      taiKhoanItem: {
        id: id_tai_khoan,
        ma_tai_khoan,
        ma_chi_tiet,
        ten_tai_khoan,
      },
    }: {
      taiKhoanItem: ITaiKhoanResponseBase;
    }) => {
      taiKhoanData.value.maTaiKhoan = ma_tai_khoan;
      taiKhoanData.value.tenTaiKhoan = ten_tai_khoan;
      taiKhoanData.value.id = id_tai_khoan;

      if (ma_chi_tiet) {
        taiKhoanData.value.maChiTiet = ma_chi_tiet.toString();
      }
    };

    const resetTaiKhoanData = () => {
      taiKhoanData.value = {
        id: 0,
        maTaiKhoan: "",
        maChiTiet: "",
        tenTaiKhoan: "",
      };
      taiKhoanSelectionKey.value += 1;
    };

    const clearForm = async () => {
      resetForm({
        values: {
          name: "",
          code: "",
          address: "",
          note: "",
        },
        errors: {},
        touched: {
          name: false,
          code: false,
          address: false,
          note: false,
        },
      });
      resetTaiKhoanData();

      await nextTick();
      tenKhoInputRef.value?.focus();
    };

    const fetchData = async () => {
      try {
        startLoading();
        const {
          data: {
            data: { data: khoItemsResponse },
          },
        } = await KhoService.fetchAllWarehousesByDepartmentId(
          store.getters.currentUser.departmentId
        );

        khoItems.value = khoItemsResponse;
        endLoading();
      } catch (err) {
        endLoading();
        await swalNotification(
          getErrorMsg(err, "Có lỗi xảy ra khi lấy danh sách kho"),
          "error"
        );
      }
    };

    onMounted(async () => {
      tenKhoInputRef.value?.focus();

      setBreadcrumbLinks([
        {
          text: "Kho",
          routeData: {
            name: "danh-sach-kho",
          },
        },
        {
          text: "Tạo mới",
        },
      ]);

      setCurrentPageTitle("Tạo mới kho");
      await fetchData();
    });

    const onSubmit = async (shouldContinue = false) => {
      if (!taiKhoanData.value.maChiTiet) {
        swalErrNotification(null, "Bắt buộc phải nhập đủ thông tin tài khoản");

        return;
      }

      const { valid } = await validate();

      if (valid) {
        try {
          startLoading();

          await KhoService.createNewWarehouse({
            ten: name.value,
            ma_kho: code.value,
            dia_chi: address.value,
            ghi_chu: note.value,
            id_tai_khoan: taiKhoanData.value.id,
            ma_chi_tiet: taiKhoanData.value.maChiTiet,
            ma_tai_khoan: taiKhoanData.value.maTaiKhoan,
            ten_tai_khoan: taiKhoanData.value.tenTaiKhoan,
          });

          endLoading();
          await swalNotification("Tạo kho thành công");

          if (shouldContinue && isCreating.value) {
            await clearForm();
            await fetchData();

            return;
          }

          await router.replace({
            name: "danh-sach-kho",
          });
        } catch (err) {
          endLoading();
          await swalNotification(
            getErrorMsg(err, "Có lỗi xảy ra khi tạo kho"),
            "error"
          );
        }
      }
    };

    return {
      ButtonsType,
      ButtonTypeColors,
      isCreating,
      newWarehouseValidator,
      onSubmit,
      clearForm,
      fetchData,
      tenKhoInputRef,
      name,
      nameErrorMess,
      code,
      codeErrorMess,
      address,
      addressErrorMess,
      note,
      goBack,
      khoItems,
      taiKhoanData,
      taiKhoanSelectionKey,
      onSelectTaiKhoan,
    };
  },
});
